import { useEffect, useState } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Avatar } from "@/components/Avatar";
import { DocumentGate } from "@/components/DocumentGate";
import { Home, CalendarDays, Ticket, User, LogOut, LayoutDashboard, Flame, Dumbbell, Trophy, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const BASE_TABS = [
  ["/app", "nav.dashboard", Home],
  ["/app/schedule", "nav.schedule", CalendarDays],
  ["/app/wod", "nav.wod", Flame],
  ["/app/prs", null, Trophy, { es: "Récords", en: "PR Wall", fr: "Records" }],
  ["/app/progress", null, Activity, { es: "Progreso", en: "Progress", fr: "Suivi" }],
  ["/app/credits", "nav.credits", Ticket],
  ["/app/profile", "nav.profile", User],
];

export function AppLayout({ children }) {
  const { t, tl } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [hasProgram, setHasProgram] = useState(false);

  useEffect(() => {
    api.get("/me/programs").then(({ data }) => setHasProgram((data || []).length > 0)).catch(() => {});
  }, []);

  const TABS = [...BASE_TABS];
  if (hasProgram) TABS.splice(3, 0, ["/app/program", null, Dumbbell, { es: "Mi programa", en: "My program", fr: "Mon programme" }]);
  const cols = TABS.length;

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-0">
      <DocumentGate />
      {/* top bar */}
      <header className="glass sticky top-0 z-40 border-b border-border/60">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5">
          <Link to="/app" className="font-display text-base font-bold tracking-tight sm:text-xl">Frenchies Fitness &amp; Pilates</Link>
          <div className="flex items-center gap-2">
            {(user?.role === "admin") && (
              <Button size="sm" variant="outline" data-testid="go-admin-btn" onClick={() => navigate("/admin")} className="rounded-full">
                <LayoutDashboard className="mr-1 h-4 w-4" /> {t("nav.admin")}
              </Button>
            )}
            {(user?.role === "coach") && (
              <Button size="sm" variant="outline" data-testid="go-coach-btn" onClick={() => navigate("/coach")} className="rounded-full">
                {t("nav.coach")}
              </Button>
            )}
            <LanguageSwitcher />
            <ThemeToggle />
            <button data-testid="logout-btn" onClick={logout} className="rounded-full p-2 hover:bg-accent" aria-label="logout">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* desktop side tabs */}
      <div className="mx-auto flex max-w-6xl gap-8 px-5 py-6">
        <aside className="hidden w-52 shrink-0 md:block">
          <nav className="sticky top-24 flex flex-col gap-1">
            <Link to="/app/profile" data-testid="sidebar-user" className="mb-3 flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5 transition-colors hover:bg-accent">
              <Avatar userId={user?.user_id} name={user?.name || user?.first_name} size={40} updatedAt={user?.avatar_updated_at} />
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold">{user?.first_name || user?.name || "—"}</div>
                <div className="truncate text-xs capitalize text-muted-foreground">{user?.role}</div>
              </div>
            </Link>
            {TABS.map(([to, key, Icon, label]) => (
              <NavLink key={to} to={to} end={to === "/app"} data-testid={`sidenav-${to.split("/").pop()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                    isActive ? "bg-primary text-primary-foreground" : "hover:bg-accent"
                  }`
                }>
                <Icon className="h-5 w-5" /> {label ? tl(label) : t(key)}
              </NavLink>
            ))}
          </nav>
        </aside>
        <div className="min-w-0 flex-1">{children}</div>
      </div>

      {/* mobile bottom nav */}
      <nav className="glass fixed bottom-0 left-0 z-40 w-full border-t border-border/60 md:hidden">
        <div className="grid" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
          {TABS.map(([to, key, Icon, label]) => (
            <NavLink key={to} to={to} end={to === "/app"} data-testid={`bottomnav-${to.split("/").pop()}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`
              }>
              <Icon className="h-6 w-6" /> {label ? tl(label) : t(key)}
            </NavLink>
          ))}
        </div>
      </nav>

      <WhatsAppButton />
    </div>
  );
}
